import { useState } from 'react'
import { useAuth } from '@/lib/auth/auth-context'
import { GREY_300, GREY_400, WARNING, WHITE, errorBlockStyle, primaryButtonStyle } from '@/lib/genesis'

/**
 * Minimal account-menu form for changing the current operator's password
 * (ADR-118/ADR-119 Phase 7). Only meaningful in COOKIE (password-login) mode:
 * the endpoint is session+CSRF gated, so a key-mode operator has no password
 * session to change — RootLayout only surfaces the trigger in cookie mode.
 *
 * On success the server revokes ALL of the operator's sessions (including this
 * one) and clears the cookies; auth.changePassword() therefore logs the SPA
 * out and returns to the login screen. That is the intended UX: "you changed
 * your password, other sessions were signed out, sign back in here" — the note
 * below sets that expectation before submit.
 *
 * Three-surface UI alignment pass (three-surface-ui-alignment): restyled
 * onto Genesis tokens + glass-card idiom; behavior unchanged.
 */
export function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const { changePassword } = useAuth()
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const mismatch = confirm.length > 0 && newPassword !== confirm

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (newPassword !== confirm) {
      setError('New password and confirmation do not match.')
      return
    }
    setSubmitting(true)
    try {
      // On success this resolves by logging out (server revoked all sessions);
      // the app transitions to the login screen, so there is nothing to close.
      await changePassword(oldPassword, newPassword)
      setOldPassword('')
      setNewPassword('')
      setConfirm('')
    } catch (err) {
      // Surface the server's specific message (wrong current password, weak
      // new password per the platform policy, etc.).
      setError(err instanceof Error ? err.message : 'Could not change password.')
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <form onSubmit={handleSubmit} className="glass-card w-full max-w-sm p-5">
        <h2 className="font-sub mb-2 text-base font-semibold" style={{ color: WHITE }}>Change password</h2>
        <p className="mb-4 text-sm" style={{ color: GREY_400 }}>
          Changing your password signs out your other sessions. You'll sign in
          again here with the new password.
        </p>

        <label htmlFor="cp-old" className="mb-1 block text-xs font-medium" style={{ color: GREY_400 }}>
          Current password
        </label>
        <input
          id="cp-old"
          type="password"
          autoComplete="current-password"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          className="glass-input mb-3 w-full px-3 py-2 text-sm"
          style={{ color: WHITE }}
          required
        />

        <label htmlFor="cp-new" className="mb-1 block text-xs font-medium" style={{ color: GREY_400 }}>
          New password
        </label>
        <input
          id="cp-new"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="glass-input mb-3 w-full px-3 py-2 text-sm"
          style={{ color: WHITE }}
          required
        />

        <label htmlFor="cp-confirm" className="mb-1 block text-xs font-medium" style={{ color: GREY_400 }}>
          Confirm new password
        </label>
        <input
          id="cp-confirm"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="glass-input mb-3 w-full px-3 py-2 text-sm"
          style={{ color: WHITE }}
          required
        />

        {mismatch && (
          <p className="mb-3 text-xs" style={{ color: WARNING }}>Passwords do not match.</p>
        )}
        {error && (
          <div className="mb-3 px-2 py-1.5" style={errorBlockStyle}>
            <p className="text-xs" style={{ color: WHITE }}>{error}</p>
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="glass-button flex-1 rounded-lg py-2 text-sm disabled:opacity-50"
            style={{ color: GREY_300 }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              submitting ||
              oldPassword.length === 0 ||
              newPassword.length === 0 ||
              confirm.length === 0 ||
              mismatch
            }
            className="flex-1 rounded-lg py-2 text-sm font-semibold disabled:opacity-50"
            style={primaryButtonStyle}
          >
            {submitting ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </form>
    </div>
  )
}
